/**
 * Schreibstube (Pilot 11.10.2026) – REST-Client. Erfasser (Akteur) bestimmt der Server aus der Anmeldung; der Absender wird gewaehlt.
 */
import { authAPI } from './authAPI';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const BASE = `${BACKEND_URL}/api/schreibstube`;

const warte = (ms) => new Promise((res) => { setTimeout(res, ms); });

/** Lesende Anfragen (GET) werden bei 429 (nginx-Rate-Limit, z. B. direkt nach einer schweren Seite) kurz wiederholt; Schreibvorgaenge nie. */
export async function holeMitWiederholung(url, opts = {}, fetcher = (u, o) => authAPI.apiFetch(u, o), pausen = [700, 1500, 3000]) {
  const lesend = !opts.method || opts.method === 'GET';
  let r = await fetcher(url, opts);
  for (let i = 0; lesend && r.status === 429 && i < pausen.length; i += 1) {
    await warte(pausen[i]);
    r = await fetcher(url, opts);
  }
  return r;
}

async function json(url, opts = {}) {
  const r = await holeMitWiederholung(url, opts);
  if (!r.ok) {
    let detail = 'Korrespondenz: Fehler';
    try { detail = (await r.json()).detail || detail; } catch { /* kein JSON */ }
    const e = new Error(detail);
    e.status = r.status;
    throw e;
  }
  return r.json();
}

async function pdf(url, opts = {}) {
  const r = await holeMitWiederholung(url, opts);
  if (!r.ok) {
    let detail = 'Vorschau nicht möglich';
    try { detail = (await r.json()).detail || detail; } catch { /* kein JSON */ }
    throw new Error(detail);
  }
  return r.blob();
}

const post = (body) => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export const schreibstubeAPI = {
  klienten: (q) => json(`${BASE}/klienten?q=${encodeURIComponent(q)}`),
  kontext: (klientId) => json(`${BASE}/kontext/${encodeURIComponent(klientId)}`),
  vorschau: (body) => pdf(`${BASE}/vorschau`, post(body)),
  entwuerfe: (klientId) => json(`${BASE}/entwuerfe${klientId ? `?klient_id=${encodeURIComponent(klientId)}` : ''}`),
  entwurf: (id) => json(`${BASE}/entwuerfe/${encodeURIComponent(id)}`),
  anlegen: (body) => json(`${BASE}/entwuerfe`, post(body)),
  speichern: (id, body) => json(`${BASE}/entwuerfe/${encodeURIComponent(id)}`, { ...post(body), method: 'PUT' }),
  uebernehmen: (id) => json(`${BASE}/entwuerfe/${encodeURIComponent(id)}/uebernehmen`, post({})),
  entwurfPdf: (id) => pdf(`${BASE}/entwuerfe/${encodeURIComponent(id)}/pdf`),
  versandvorschau: (id) => json(`${BASE}/entwuerfe/${encodeURIComponent(id)}/versandvorschau`),
  versandlog: (id) => json(`${BASE}/entwuerfe/${encodeURIComponent(id)}/versandlog`),
  zeoVorschlag: (messageId) => json(`${BASE}/zeo-vorschlag/${encodeURIComponent(messageId)}`),
  // Korrespondenz (25.10.2026)
  versandwege: () => json(`${BASE}/versandwege`),
  anlagen: (klientId, q) => json(`${BASE}/anlagen/${encodeURIComponent(klientId)}${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  altbestand: (klientId) => json(`${BASE}/altbestand/${encodeURIComponent(klientId)}`),
  altUebernehmen: (svId) => json(`${BASE}/altbestand/schriftverkehr/${encodeURIComponent(svId)}/uebernehmen`, post({})),
  entwurfDocx: (id) => pdf(`${BASE}/entwuerfe/${encodeURIComponent(id)}/docx`),
  // bestehende Vorlagenverwaltung (schriftverkehr_vorlagen) -- keine zweite Vorlagenablage
  vorlageSpeichern: (body) => json(`${BACKEND_URL}/api/schriftverkehr/vorlagen`, post(body)),
  senden: (id, body) => json(`${BACKEND_URL}/api/briefe/${encodeURIComponent(id)}/senden`, post(body)),
  alsPostVersendet: (id, body) => json(`${BACKEND_URL}/api/briefe/${encodeURIComponent(id)}/als-post-versendet`, post(body)),
  faxen: (id, body) => json(`${BACKEND_URL}/api/briefe/${encodeURIComponent(id)}/faxen`, post(body)),
  textbausteine: () => json(`${BASE}/textbausteine`),
  platzhalter: (body) => json(`${BASE}/platzhalter`, post(body)),
  formulieren: (body) => json(`${BASE}/formulieren`, post(body)),
  kontaktVorschau: (body) => json(`${BASE}/kontakt/vorschau`, post(body)),
  kontaktSpeichern: (body) => json(`${BASE}/kontakt/speichern`, post(body)),
  // Verbesserungen 26.10.2026
  plzOrte: (plz) => json(`${BASE}/plz/${encodeURIComponent(plz)}`),
  aufgabeAnlegen: (briefId, body) => json(`${BASE}/entwuerfe/${encodeURIComponent(briefId)}/aufgabe`, post(body)),
  anlageHochladen: (klientId, datei) => {
    const fd = new FormData();
    fd.append('datei', datei);
    return json(`${BASE}/anlagen/${encodeURIComponent(klientId)}/upload`, { method: 'POST', body: fd });
  },
  // eigene Textbausteine = Kategorie „textbaustein“ der bestehenden Vorlagenverwaltung (schriftverkehr_vorlagen) -- keine zweite Sammlung
  eigeneBausteine: () => json(`${BACKEND_URL}/api/schriftverkehr/vorlagen?kategorie=textbaustein`),
  bausteinSpeichern: (body) => json(`${BACKEND_URL}/api/schriftverkehr/vorlagen`, post({ beschreibung: 'Textbaustein aus der Korrespondenz', ...body, kategorie: 'textbaustein' })),
  bausteinAendern: (id, body) => json(`${BACKEND_URL}/api/schriftverkehr/vorlagen/${encodeURIComponent(id)}`, { ...post(body), method: 'PUT' }),
};
